import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteScheduleTemplate,
  getScheduleTemplateStats,
  getScheduleTemplates,
} from "../../../../api/scheduleTemplatesApi";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import { hasAdmsScheduleTemplatesAccess } from "../scheduleAccess";
import "../ProjectsSchedule.css";
import "../ProjectsScheduleDetail.css";
import "./ScheduleTemplates.css";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const ScheduleTemplates = () => {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const isAdms = hasAdmsScheduleTemplatesAccess(userData);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getScheduleTemplates();
      setTemplates(data);
    } catch (e) {
      setError(e?.message || "Не удалось загрузить шаблоны");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdms) return;
    loadTemplates();
  }, [isAdms]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((tpl) =>
      `${tpl.name || ""} ${tpl.description || ""} ${tpl.building_type || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [templates, query]);

  const handleDelete = async (template) => {
    const ok = window.confirm(
      `Удалить шаблон «${template.name}»? Это не изменит уже заполненные графики проектов.`,
    );
    if (!ok) return;
    try {
      setDeletingId(template.id);
      await deleteScheduleTemplate(template.id);
      await loadTemplates();
    } catch (e) {
      setError(e?.message || "Не удалось удалить шаблон");
    } finally {
      setDeletingId(null);
    }
  };

  if (!isAdms) {
    return (
      <div className="ps-page">
        <div className="ps-state ps-state--error">
          Раздел доступен только пользователям с уровнем доступа АДМС.
        </div>
      </div>
    );
  }

  return (
    <div className="ps-page pst-page">
      <div className="psd-header pst-header">
        <button
          type="button"
          className="psd-back"
          onClick={() => navigate("/projects/schedule")}
          aria-label="Назад к графику проектов"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="psd-title">Шаблоны графиков</h1>
        <button
          type="button"
          className="pst-primary-btn"
          onClick={() => navigate("/projects/schedule/templates/new")}
        >
          Создать шаблон
        </button>
      </div>

      <div className="ps-table-card pst-list-card">
        <p className="pst-editor-note pst-storage-note">
          Шаблоны общие: их видят все, у кого есть доступ к графику проекта.
          Создавать и править может только АДМС.
        </p>
        <div className="pst-list-toolbar">
          <input
            type="search"
            className="pst-select-search"
            placeholder="Поиск шаблона..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="ps-state">Загрузка...</div>
        ) : error ? (
          <div className="ps-state ps-state--error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="ps-state">
            Шаблонов пока нет. Создайте первый — затем его можно будет применить
            к проекту целиком.
          </div>
        ) : (
          <div className="ps-table-wrap">
            <table className="ps-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Тип здания</th>
                  <th>Разделы</th>
                  <th>Работы / вехи</th>
                  <th>Обновлён</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tpl) => {
                  const stats = getScheduleTemplateStats(tpl);
                  return (
                    <tr key={tpl.id}>
                      <td>
                        <div className="pst-list-name">{tpl.name}</div>
                        {tpl.description && (
                          <div className="pst-list-desc">{tpl.description}</div>
                        )}
                      </td>
                      <td>{tpl.building_type || "—"}</td>
                      <td>{stats.sectionsCount}</td>
                      <td>
                        {stats.milestonesCount == null
                          ? stats.itemsCount
                          : `${stats.workTypesCount} / ${stats.milestonesCount}`}
                      </td>
                      <td>{formatDate(tpl.updated_at)}</td>
                      <td>
                        <div className="pst-list-actions">
                          <button
                            type="button"
                            className="psd-add-btn"
                            onClick={() =>
                              navigate(`/projects/schedule/templates/${tpl.id}`)
                            }
                          >
                            Редактировать
                          </button>
                          <button
                            type="button"
                            className="pst-danger-btn"
                            disabled={deletingId === tpl.id}
                            onClick={() => handleDelete(tpl)}
                          >
                            {deletingId === tpl.id ? "Удаление..." : "Удалить"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleTemplates;
